import { api } from "shared/redux/api"

const BASE = "/private/procurement"

// Drop null / undefined / "" / empty arrays so we don't ship the
// `objectToQueryString` bug into procurement list filters (see brief §10).
export const buildListParams = (params = {}) => {
  const out = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue
    if (Array.isArray(v) && v.length === 0) continue
    out[k] = v
  }
  return out
}

// Shared filter set added to the OR/PO/PR list endpoints (order-requests,
// purchases, payment-requests): from/to date (created_at), category, region,
// center, department, item, supplier. Takes the page's camelCase filter
// state and maps it to the BE's wire param names. `item` is a List<Long> on
// the BE side, but axios serializes an array as `item[]=1&item[]=2`, which
// the BE doesn't parse — join it into a single comma-separated `item=1,2,3`
// instead (also accepted per the API contract). `department` is mapped here
// even though no page renders that field yet (FE has no ID-based source for
// it) so a future page only needs to add the FilterBar field.
export const buildProcurementListFilters = ({
  fromDate,
  toDate,
  category,
  region,
  center,
  department,
  item,
  supplier,
  ...rest
} = {}) =>
  buildListParams({
    ...rest,
    from_date: fromDate,
    to_date: toDate,
    category,
    region,
    center,
    department,
    item: Array.isArray(item) ? (item.length ? item.join(",") : undefined) : item,
    supplier,
  })

const procurementApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Requests
    getProcurementRequests: build.query({
      query: (params = {}) => ({
        url: `${BASE}/requests`,
        params: buildListParams(params),
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: "PR", id })),
              { type: "PR", id: "LIST" },
            ]
          : [{ type: "PR", id: "LIST" }],
    }),
    getProcurementRequest: build.query({
      query: (id) => ({ url: `${BASE}/requests/${id}` }),
      providesTags: (_r, _e, id) => [{ type: "PR", id }],
    }),
    createProcurementRequest: build.mutation({
      query: (body) => ({
        url: `${BASE}/requests`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PR", id: "LIST" }],
    }),
    updateProcurementRequest: build.mutation({
      query: ({ id, body }) => ({
        url: `${BASE}/requests/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PR", id },
        { type: "PR", id: "LIST" },
      ],
    }),
    deleteProcurementRequest: build.mutation({
      query: (id) => ({
        url: `${BASE}/requests/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PR", id: "LIST" }],
    }),
    submitProcurementRequest: build.mutation({
      query: (id) => ({
        url: `${BASE}/requests/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PR", id },
        { type: "PR", id: "LIST" },
      ],
    }),
    resubmitProcurementRequest: build.mutation({
      query: (id) => ({
        url: `${BASE}/requests/${id}/resubmit`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PR", id },
        { type: "PR", id: "LIST" },
      ],
    }),
    markPaymentReady: build.mutation({
      query: (id) => ({
        url: `${BASE}/requests/${id}/payment-ready`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PR", id },
        { type: "PR", id: "LIST" },
      ],
    }),

    // Documents
    listProcurementDocuments: build.query({
      query: (id) => ({ url: `${BASE}/requests/${id}/documents` }),
      providesTags: (_r, _e, id) => [{ type: "PR-DOCS", id }],
    }),
    uploadProcurementDocument: build.mutation({
      query: ({ id, file, docType }) => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("docType", docType)
        return {
          url: `${BASE}/requests/${id}/documents`,
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: (_r, _e, { id }) => [{ type: "PR-DOCS", id }],
    }),

    // Approval log (Related Approval tab, read-only)
    getApprovalLog: build.query({
      query: (id) => ({ url: `${BASE}/requests/${id}/approval-log` }),
      providesTags: (_r, _e, id) => [{ type: "PR-APPROVAL", id }],
    }),

    // GRN
    getGrn: build.query({
      query: (id) => ({ url: `${BASE}/requests/${id}/grn` }),
      providesTags: (_r, _e, id) => [{ type: "PR-GRN", id }],
    }),
    saveGrn: build.mutation({
      query: ({ id, lines }) => ({
        url: `${BASE}/requests/${id}/grn`,
        method: "PUT",
        body: lines,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "PR-GRN", id }],
    }),
    markGrnDelivered: build.mutation({
      query: (id) => ({
        url: `${BASE}/requests/${id}/grn/mark-delivered`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PR-GRN", id },
        { type: "PR", id },
      ],
    }),

    // Dropdowns
    getSuppliers: build.query({
      query: (params = {}) => ({
        url: `${BASE}/suppliers`,
        params: buildListParams(params),
      }),
    }),
    getBudgetCategories: build.query({
      query: (params = { activeOnly: true }) => ({
        url: `${BASE}/v1/budget-categories`,
        params: buildListParams(params),
      }),
    }),
    // Versioned per the 2026-08-2x procurement path cleanup — this module's
    // other endpoints (legacy PR/GRN/PO flow) aren't part of that migration
    // and stay on the unversioned BASE.
    getDepartments: build.query({
      query: () => ({ url: `${BASE}/v1/departments` }),
    }),
    // Departments mapped to the caller's roles; exactly one row has isDefault.
    getMyDepartments: build.query({
      query: (params = {}) => ({
        url: `${BASE}/v1/departments/mine`,
        params: buildListParams(params),
      }),
    }),
    getPos: build.query({
      query: (params = {}) => ({
        url: `${BASE}/pos`,
        params: buildListParams(params),
      }),
    }),
    getPoDetail: build.query({
      query: (code) => ({ url: `${BASE}/pos/${code}` }),
    }),

    // Budgets
    getBudgets: build.query({
      query: (params) => ({
        url: `${BASE}/budgets`,
        params: buildListParams(params),
      }),
    }),

    // Fallback capability signal (not wired into menu; brief §5 keeps it for later)
    getProcurementEnabled: build.query({
      query: () => ({ url: `${BASE}/enabled` }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetProcurementRequestsQuery,
  useLazyGetProcurementRequestsQuery,
  useGetProcurementRequestQuery,
  useCreateProcurementRequestMutation,
  useUpdateProcurementRequestMutation,
  useDeleteProcurementRequestMutation,
  useSubmitProcurementRequestMutation,
  useResubmitProcurementRequestMutation,
  useMarkPaymentReadyMutation,
  useListProcurementDocumentsQuery,
  useUploadProcurementDocumentMutation,
  useGetApprovalLogQuery,
  useGetGrnQuery,
  useSaveGrnMutation,
  useMarkGrnDeliveredMutation,
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
  useGetBudgetCategoriesQuery,
  useGetDepartmentsQuery,
  useGetMyDepartmentsQuery,
  useGetPosQuery,
  useLazyGetPoDetailQuery,
  useGetBudgetsQuery,
  useLazyGetProcurementEnabledQuery,
} = procurementApi
